import React from 'react';
import classnames from 'classnames';

import styles from './toggle.css';

interface ToggleProps {
  /** The value for the toggle. Whether it is on or off */
  value: boolean;
  /** Disables toggle disallowing interaction */
  disabled?: boolean;
  /** Provides visual elements for usage on a dark environment */
  darkEnv?: boolean;
  /** Callback when clicked */
  onClick?: (e?: React.MouseEvent) => void;
  /** Adds additional classes to the component. */
  className?: string;
  /** Provides visual styles for a smaller toggle */
  mini?: boolean;
  /** Provides a data-testid */
  'data-testid'?: string;
}

/**
 * Toggle Component
 *
 * @param {ToggleProps} param
 *
 * @returns JSX.ELEMENT
 */
export const Toggle = ({
  value,
  onClick,
  className,
  'data-testid': dataTestId,
  darkEnv = false,
  disabled = false,
  mini = false
}: ToggleProps) => {
  const handleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    /* istanbul ignore next */
    ev.preventDefault();
    onClick!();
  };

  const toggleClasses = classnames(
    {
      [styles.toggle]: !mini,
      [styles.mini]: mini,
      [styles.disabled]: disabled,
      [styles.on]: value,
      [styles.dark]: darkEnv
    },
    className
  );

  return (
    <button
      className={toggleClasses}
      aria-pressed={value}
      disabled={disabled}
      {...(!disabled && typeof onClick === 'function' ? { onClick: handleClick } : {})}
      data-testid={dataTestId}
    >
      <div className={styles.labelOn}>on</div>
      <div className={styles.switcher} />
      <div className={styles.labelOff}>off</div>
    </button>
  );
};

export default Toggle;
